import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  ChevronLeft,
  Send,
  Image as ImageIcon,
  Calendar as CalendarIcon,
  Search,
  X,
  ArrowUp,
  Trash2,
  Pencil,
  MessageSquare,
  CornerUpLeft,
} from "lucide-react";
import { toast } from "sonner";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const ChatDetail = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Message input state
  const [messageContent, setMessageContent] = useState("");
  // Prefill a quoted reply when arriving from a "reply privately" shortcut
  useEffect(() => {
    if (routerLocation.state?.prefill) {
      setMessageContent(routerLocation.state.prefill);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line
  }, [routerLocation.state, channelId]);
  const [roomNumber, setRoomNumber] = useState("");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  // Close task modal
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [closingNote, setClosingNote] = useState("");
  const [closingLoading, setClosingLoading] = useState(false);

  // Comment modal
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  // Fetch channel details
  useEffect(() => {
    const fetchChannel = async () => {
      try {
        const response = await axios.get(`${API}/channels/${channelId}`);
        setChannel(response.data);
      } catch (error) {
        toast.error("Errore nel caricamento del canale");
        navigate("/");
      }
    };
    fetchChannel();
  }, [channelId, navigate]);

  // Fetch messages for selected date
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(
        `${API}/channels/${channelId}/messages/by-date?date=${dateStr}`
      );
      setMessages(response.data);
      setSearchResults(null);
    } catch (error) {
      toast.error("Errore nel caricamento dei messaggi");
    } finally {
      setLoading(false);
    }
  }, [channelId, selectedDate]);

  useEffect(() => {
    if (channelId) {
      fetchMessages();
    }
  }, [channelId, fetchMessages]);

  const isContiAperti = channel?.channel_type === "conti_aperti";
  const isDirect = channel?.channel_type === "direct";
  const channelTitle = isDirect
    ? (channel?.member_details?.find((m) => m.id !== user?.id)?.name || "Messaggio privato")
    : (channel?.name || "Caricamento...");

  const openDirectWith = async (userId, quote) => {
    if (!userId || userId === user?.id) return;
    try {
      const res = await axios.post(`${API}/conversations/direct`, { user_id: userId });
      navigate(`/chat/${res.data.id}`, { state: quote ? { prefill: quote } : undefined });
    } catch {
      toast.error("Impossibile aprire la chat privata");
    }
  };
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setSelectedImages((prev) => [...prev, ...files]);
    
    // Create preview URLs
    const newUrls = files.map((file) => URL.createObjectURL(file));
    setImagePreviewUrls((prev) => [...prev, ...newUrls]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setImagePreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload images and return paths
  const uploadImages = async () => {
    const paths = [];
    for (const file of selectedImages) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        paths.push(response.data.path);
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Errore upload: ${file.name}`);
      }
    }
    return paths;
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    // Conti aperti validation
    if (isContiAperti) {
      if (!roomNumber.trim()) {
        toast.error("Inserisci il numero camera");
        return;
      }
      if (!checkoutDate) {
        toast.error("Inserisci la data di check-out");
        return;
      }
      
      // Check for duplicate room number in open tasks
      const existingOpen = [];
      messages.forEach((msg) => {
        (msg.room_references || []).forEach((ref) => {
          if (ref.room_number === roomNumber.trim() && ref.status === "open") {
            existingOpen.push(ref);
          }
        });
      });
      
      if (existingOpen.length > 0) {
        const closeOld = window.confirm(
          `La camera #${roomNumber.trim()} ha già un conto aperto.\n\nVuoi chiudere il conto precedente e aprirne uno nuovo?`
        );
        if (closeOld) {
          for (const ref of existingOpen) {
            try {
              await axios.patch(`${API}/rooms/close`, {
                room_reference_id: ref.id,
                closing_note: "Chiuso per nuovo check-in",
              });
            } catch {}
          }
          toast.success(`Conto precedente #${roomNumber.trim()} chiuso`);
        } else {
          return;
        }
      }
    }
    
    if (!messageContent.trim() && selectedImages.length === 0 && !isContiAperti) return;

    // Validate room/keyword field
    const trimmedRoom = roomNumber.trim();
    if (trimmedRoom && trimmedRoom.includes(",")) {
      const parts = trimmedRoom.split(",").filter((p) => p.trim());
      if (parts.length > 5) {
        toast.error("Massimo 5 numeri camera separati da virgola");
        return;
      }
    }

    setSending(true);
    try {
      let imagePaths = [];
      if (selectedImages.length > 0) {
        imagePaths = await uploadImages();
      }

      const payload = {
        content: isContiAperti ? (messageContent.trim() || `Camera ${roomNumber.trim()} - Check-out ${checkoutDate}`) : messageContent.trim(),
        pinned: false,
        images: imagePaths,
        room_number: roomNumber.trim() || null,
        task_start_date: roomNumber.trim() ? new Date(selectedDate).toISOString() : null,
        checkout_date: checkoutDate || null,
      };

      await axios.post(`${API}/channels/${channelId}/messages`, payload);

      // Reset form
      setMessageContent("");
      setRoomNumber("");
      setCheckoutDate("");
      setSelectedImages([]);
      imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
      setImagePreviewUrls([]);

      // Refresh messages
      fetchMessages();
    } catch (error) {
      toast.error("Errore nell'invio del messaggio");
    } finally {
      setSending(false);
    }
  };

  // Search messages
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await axios.post(`${API}/channels/${channelId}/search`, {
        query: searchQuery,
      });
      setSearchResults(response.data);
    } catch (error) {
      toast.error("Errore nella ricerca");
    }
  };

  // Open close task modal
  const openCloseModal = (task, messageId) => {
    setSelectedTask({ ...task, messageId });
    setClosingNote("");
    setCloseModalOpen(true);
  };

  // Open comment modal
  const openCommentModal = (task, messageId) => {
    setSelectedTask({ ...task, messageId });
    setCommentText("");
    setCommentModalOpen(true);
  };

  // Add comment to task
  const handleAddComment = async () => {
    if (!selectedTask || !commentText.trim()) return;

    setCommentLoading(true);
    try {
      await axios.post(`${API}/rooms/comment`, {
        room_reference_id: selectedTask.id,
        comment: commentText.trim(),
      });

      toast.success(`Commento aggiunto alla camera #${selectedTask.room_number}`);
      setCommentModalOpen(false);
      fetchMessages();
    } catch (error) {
      toast.error("Errore nell'aggiunta del commento");
    } finally {
      setCommentLoading(false);
    }
  };

  // Close task
  const handleCloseTask = async () => {
    if (!selectedTask) return;

    setClosingLoading(true);
    try {
      await axios.patch(`${API}/rooms/close`, {
        room_reference_id: selectedTask.id,
        closing_note: closingNote.trim() || null,
      });

      toast.success(`Compito #${selectedTask.room_number} chiuso`);
      setCloseModalOpen(false);
      fetchMessages();
    } catch (error) {
      toast.error("Errore nella chiusura del compito");
    } finally {
      setClosingLoading(false);
    }
  };

  // Delete task (only creator)
  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/rooms/${taskId}`);
      toast.success("Compito eliminato");
      fetchMessages();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nell'eliminazione");
    }
  };

  // Delete comment (only author)
  const handleDeleteComment = async (taskId, commentId) => {
    try {
      await axios.delete(`${API}/rooms/${taskId}/comments/${commentId}`);
      toast.success("Commento eliminato");
      fetchMessages();
    } catch (error) {
      toast.error("Errore nell'eliminazione del commento");
    }
  };

  // Edit task (room_number + message text)
  const handleEditTask = async (taskId, newRoomNumber, newContent) => {
    try {
      const payload = {};
      if (newRoomNumber?.trim()) payload.room_number = newRoomNumber.trim();
      if (newContent !== undefined) payload.content = newContent.trim();
      
      await axios.patch(`${API}/rooms/${taskId}`, payload);
      toast.success("Compito modificato");
      fetchMessages();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nella modifica");
    }
  };

  // Edit comment
  const handleEditComment = async (taskId, commentId, newText) => {
    if (!newText?.trim()) return;
    try {
      await axios.patch(`${API}/rooms/${taskId}/comments/${commentId}`, { text: newText.trim() });
      toast.success("Commento modificato");
      fetchMessages();
    } catch (error) {
      toast.error("Errore nella modifica del commento");
    }
  };

  // Date navigation
  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date());
    setCalendarOpen(false);
  };

  // Format date for header
  const formatHeaderDate = () => {
    if (isToday(selectedDate)) return "Oggi";
    if (isYesterday(selectedDate)) return "Ieri";
    return format(selectedDate, "d MMMM yyyy", { locale: it });
  };

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach((msg) => {
      const date = format(parseISO(msg.timestamp), "yyyy-MM-dd");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });
    return groups;
  };

  // Render room pill
  const RoomPill = ({ task, messageId, messageContent = "", inline = false }) => {
    const isOpen = task.status === "open";
    const [showActions, setShowActions] = useState(false);
    
    if (isOpen) {
      return (
        <div className={`inline-flex items-center relative ${inline ? "mx-1" : ""}`}>
          <button
            onClick={() => setShowActions(!showActions)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-xs font-bold 
              bg-amber-100 text-amber-800 border border-amber-300 
              hover:bg-amber-200 hover:shadow-md transition-all cursor-pointer room-pill-open`}
            data-testid={`room-pill-${task.room_number}`}
          >
            #{task.room_number}
            {task.comments && task.comments.length > 0 && (
              <span className="ml-1 bg-amber-300 text-amber-900 rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
                {task.comments.length}
              </span>
            )}
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-[60]" 
                onClick={() => setShowActions(false)}
              />
              <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-[70] min-w-[160px]">
                <button
                  onClick={() => {
                    setShowActions(false);
                    openCommentModal(task, messageId);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                  data-testid={`add-comment-${task.room_number}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Aggiungi nota
                </button>
                <button
                  onClick={() => {
                    setShowActions(false);
                    openCloseModal(task, messageId);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
                  data-testid={`close-task-${task.room_number}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Chiudi compito
                </button>
                {(task.created_by === user?.id || ["admin", "super_admin"].includes(user?.role)) && (
                  <button
                    onClick={() => {
                      setShowActions(false);
                      setTimeout(() => {
                        const newRoom = window.prompt("Parola chiave / Camera:", task.room_number);
                        if (newRoom === null) return;
                        const newText = window.prompt("Testo messaggio:", messageContent);
                        if (newText === null) return;
                        handleEditTask(task.id, newRoom, newText);
                      }, 100);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                    data-testid={`edit-task-${task.room_number}`}
                  >
                    <Pencil className="w-4 h-4" />
                    Modifica compito
                  </button>
                )}
                {(task.created_by === user?.id || ["admin", "super_admin"].includes(user?.role)) && (
                  <button
                    onClick={() => {
                      setShowActions(false);
                      setTimeout(() => {
                        if (window.confirm("Eliminare questo compito?")) {
                          handleDeleteTask(task.id);
                        }
                      }, 100);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    data-testid={`delete-task-${task.room_number}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Elimina compito
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-xs font-bold 
          bg-emerald-50 text-emerald-700 border border-emerald-200 line-through
          ${inline ? "mx-1" : ""}`}
        data-testid={`room-pill-closed-${task.room_number}`}
      >
        #{task.room_number}
      </span>
    );
  };

  // Render message content with inline room pills
  const renderMessageContent = (content, roomRefs) => {
    const inlineRoomNumbers = new Set();
    roomRefs.forEach((ref) => {
      if (content.includes(`#${ref.room_number}`)) {
        inlineRoomNumbers.add(ref.room_number);
      }
    });

    if (inlineRoomNumbers.size === 0) {
      return <span>{content}</span>;
    }

    // Match #digits (room numbers only in text)
    const pattern = /#(\d{3,4})\b/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      const roomNum = match[1];
      const ref = roomRefs.find((r) => r.room_number === roomNum);
      if (ref) {
        parts.push(
          <RoomPill key={`inline-${ref.id}`} task={ref} inline={true} />
        );
      } else {
        parts.push(match[0]);
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return <span>{parts}</span>;
  };

  // Get room refs not in text (for showing below message)
  const getNonInlineRoomRefs = (content, roomRefs) => {
    return roomRefs.filter((ref) => !content.includes(`#${ref.room_number}`));
  };

  const displayMessages = searchResults !== null ? searchResults : messages;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(isDirect || channel?.channel_type === "group" ? "/chat" : "/")}
            className="text-stone-600 hover:text-stone-900"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          <div className="flex-1 min-w-0">
            <h1 className="font-outfit font-semibold text-stone-900 truncate">
              {channelTitle}
            </h1>
            <p className="text-xs text-stone-500">{formatHeaderDate()}</p>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSearch(!showSearch)}
              className="text-stone-500 hover:text-stone-700"
              data-testid="search-toggle"
            >
              <Search className="w-5 h-5" />
            </Button>

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-stone-500 hover:text-stone-700"
                  data-testid="calendar-button"
                >
                  <CalendarIcon className="w-5 h-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-2 border-b">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToToday}
                    className="w-full"
                    data-testid="today-button"
                  >
                    Oggi
                  </Button>
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  locale={it}
                  data-testid="calendar"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Search bar */}
        {showSearch && (
          <div className="px-4 pb-3 flex gap-2">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Cerca messaggi..."
              className="flex-1 rounded-full bg-stone-100 border-stone-200"
              data-testid="search-input"
            />
            <Button
              onClick={handleSearch}
              size="sm"
              className="rounded-full"
              data-testid="search-button"
            >
              Cerca
            </Button>
            {searchResults !== null && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSearchResults(null);
                  setSearchQuery("");
                }}
                className="text-stone-500"
                data-testid="clear-search"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-4 pb-56">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-stone-200" />
                  <div className="flex-1">
                    <div className="h-3 bg-stone-200 rounded w-1/4 mb-2" />
                    <div className="h-4 bg-stone-100 rounded w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : displayMessages.length === 0 ? (
          <div className="text-center py-12 text-stone-400">
            <p>
              {searchResults !== null
                ? "Nessun risultato trovato"
                : isContiAperti ? "Nessun conto aperto" : "Nessun messaggio per questa data"}
            </p>
          </div>
        ) : isContiAperti ? (
          /* CONTI APERTI - Grid view */
          <div>
            {/* Collect all room refs from all messages */}
            {(() => {
              const allRefs = [];
              displayMessages.forEach((msg) => {
                (msg.room_references || []).forEach((ref) => {
                  allRefs.push({ ...ref, msgContent: msg.content, msgId: msg.id, userName: msg.user_name, timestamp: msg.timestamp });
                });
              });
              
              const openRefs = allRefs.filter(r => r.status === "open")
                .sort((a, b) => {
                  const numA = parseInt(a.room_number) || 0;
                  const numB = parseInt(b.room_number) || 0;
                  return numA - numB;
                });
              const closedRefs = allRefs.filter(r => r.status === "closed")
                .sort((a, b) => {
                  const numA = parseInt(a.room_number) || 0;
                  const numB = parseInt(b.room_number) || 0;
                  return numA - numB;
                });

              return (
                <div className="space-y-4">
                  {openRefs.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Conti aperti ({openRefs.length})</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {openRefs.map((ref) => (
                          <div key={ref.id}>
                            <button
                              onClick={() => setExpandedTaskId(expandedTaskId === ref.id ? null : ref.id)}
                              className={`w-full rounded-xl border-2 p-3 text-left transition-all ${
                                expandedTaskId === ref.id
                                  ? "border-amber-400 bg-amber-50 shadow-md"
                                  : "border-amber-200 bg-white hover:border-amber-300 hover:shadow-sm"
                              }`}
                            >
                              <p className="font-mono font-bold text-lg text-stone-900">#{ref.room_number}</p>
                              {ref.checkout_date && (() => {
                                try {
                                  return (
                                    <p className="text-xs text-blue-600 font-medium mt-0.5">
                                      C/O {format(parseISO(ref.checkout_date), "d MMM", { locale: it })}
                                    </p>
                                  );
                                } catch { return null; }
                              })()}
                              {ref.comments && ref.comments.length > 0 && (
                                <p className="text-[10px] text-stone-400 mt-1">{ref.comments.length} nota/e</p>
                              )}
                            </button>
                            
                            {/* Expanded panel */}
                            {expandedTaskId === ref.id && (
                              <div className="mt-1 rounded-xl border border-stone-200 bg-white p-3 shadow-sm space-y-2">
                                {/* Message text */}
                                {ref.msgContent && (
                                  <p className="text-sm text-stone-600">{ref.msgContent}</p>
                                )}
                                
                                {/* Info */}
                                <p className="text-[10px] text-stone-400">
                                  {ref.userName} {ref.timestamp && (() => { try { return `• ${format(parseISO(ref.timestamp), "d MMM HH:mm", { locale: it })}`; } catch { return ""; } })()}
                                </p>
                                
                                {/* Notes/Comments */}
                                {ref.comments && ref.comments.length > 0 && (
                                  <div className="space-y-1 border-t border-stone-100 pt-2">
                                    <p className="text-[10px] font-medium text-stone-500 uppercase">Note</p>
                                    {ref.comments.map((comment) => (
                                      <div key={comment.id} className="text-xs bg-stone-50 rounded p-1.5 group/comment">
                                        <div className="flex items-center gap-1 text-stone-400">
                                          <span className="font-medium text-stone-600">{comment.user_name}</span>
                                          <span>•</span>
                                          <span>{(() => { try { return format(parseISO(comment.created_at), "d MMM HH:mm", { locale: it }); } catch { return ""; } })()}</span>
                                          {(comment.user_id === user?.id || ["admin", "super_admin"].includes(user?.role)) && (
                                            <span className="ml-auto flex gap-1 opacity-0 group-hover/comment:opacity-100 transition-opacity">
                                              <button onClick={() => { const t = window.prompt("Modifica:", comment.text); if (t) handleEditComment(ref.id, comment.id, t); }} className="text-blue-400 hover:text-blue-600"><Pencil className="w-3 h-3" /></button>
                                              <button onClick={() => handleDeleteComment(ref.id, comment.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-stone-700 mt-0.5">{comment.text}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-1.5 border-t border-stone-100 pt-2">
                                  <button
                                    onClick={() => { openCommentModal(ref, ref.msgId); setExpandedTaskId(null); }}
                                    className="text-xs px-2.5 py-1.5 rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200"
                                  >
                                    + Nota
                                  </button>
                                  <button
                                    onClick={() => { openCloseModal(ref, ref.msgId); setExpandedTaskId(null); }}
                                    className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                  >
                                    Chiudi
                                  </button>
                                  {(ref.created_by === user?.id || ["admin", "super_admin"].includes(user?.role)) && (
                                    <>
                                      <button
                                        onClick={() => {
                                          setExpandedTaskId(null);
                                          setTimeout(() => {
                                            const r = window.prompt("Camera:", ref.room_number);
                                            if (r === null) return;
                                            const t = window.prompt("Testo:", ref.msgContent);
                                            if (t === null) return;
                                            handleEditTask(ref.id, r, t);
                                          }, 100);
                                        }}
                                        className="text-xs px-2.5 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                                      >
                                        Modifica
                                      </button>
                                      <button
                                        onClick={() => {
                                          setExpandedTaskId(null);
                                          setTimeout(() => {
                                            if (window.confirm("Eliminare?")) handleDeleteTask(ref.id);
                                          }, 100);
                                        }}
                                        className="text-xs px-2.5 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                                      >
                                        Elimina
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  
                  {closedRefs.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-stone-400 uppercase tracking-wide mt-4">Chiusi ({closedRefs.length})</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {closedRefs.map((ref) => (
                          <div
                            key={ref.id}
                            className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 opacity-60"
                          >
                            <p className="font-mono font-bold text-lg text-stone-500 line-through">#{ref.room_number}</p>
                            {ref.checkout_date && (() => {
                              try {
                                return (
                                  <p className="text-xs text-emerald-600 mt-0.5">
                                    C/O {format(parseISO(ref.checkout_date), "d MMM", { locale: it })}
                                  </p>
                                );
                              } catch { return null; }
                            })()}
                            {ref.closing_note && (
                              <p className="text-[10px] text-stone-400 mt-1 italic">"{ref.closing_note}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="space-y-4" data-testid="messages-container">
            {displayMessages.map((msg) => (
              <div
                key={msg.id}
                className="message-enter"
                data-testid={`message-${msg.id}`}
              >
                {/* Carried forward indicator */}
                {msg.is_carried_forward && (
                  <div className="flex items-center gap-1 text-amber-600 text-xs mb-1 ml-11">
                    <ArrowUp className="w-3 h-3" />
                    <span>Portato avanti dai giorni precedenti</span>
                  </div>
                )}

                <div className="flex gap-3">
                  {/* Avatar (click for private-chat shortcut) */}
                  {(!isDirect && msg.user_id && msg.user_id !== user?.id) ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="w-8 h-8 rounded-full bg-stone-300 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 hover:ring-2 hover:ring-amber-300 transition"
                          data-testid={`avatar-${msg.id}`}
                          title={`Scrivi a ${msg.user_name}`}
                        >
                          {msg.user_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-56 p-1">
                        <p className="px-3 pt-2 pb-1 text-xs text-stone-400 truncate">{msg.user_name}</p>
                        <button
                          onClick={() => openDirectWith(msg.user_id)}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-stone-100 flex items-center gap-2"
                          data-testid={`dm-open-${msg.id}`}
                        >
                          <MessageSquare className="w-4 h-4 text-indigo-500" /> Apri chat privata
                        </button>
                        <button
                          onClick={() => openDirectWith(msg.user_id, `↩︎ In risposta a ${msg.user_name}: "${msg.content}"\n\n`)}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-stone-100 flex items-center gap-2"
                          data-testid={`dm-reply-${msg.id}`}
                        >
                          <CornerUpLeft className="w-4 h-4 text-amber-500" /> Rispondi in privato
                        </button>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-stone-300 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {msg.user_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    {/* User name and time */}
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-semibold text-stone-900 text-sm">
                        {msg.user_name}
                      </span>
                      <span className="text-xs text-stone-400">
                        {format(parseISO(msg.timestamp), "HH:mm")}
                      </span>
                    </div>

                    {/* Tasks with message - each in its own card */}
                    {getNonInlineRoomRefs(msg.content, msg.room_references)
                      .length > 0 ? (
                      <div className="space-y-1.5">
                        {getNonInlineRoomRefs(msg.content, msg.room_references).map((ref) => (
                          <div
                            key={ref.id}
                            className={`rounded-lg border p-2.5 ${
                              ref.status === "closed"
                                ? "border-emerald-200 bg-emerald-50/30"
                                : "border-amber-200 bg-amber-50/30"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <RoomPill task={ref} messageId={msg.id} messageContent={msg.content} />
                              {ref.checkout_date && (() => {
                                try {
                                  return (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                      C/O {format(parseISO(ref.checkout_date), "d MMM", { locale: it })}
                                    </span>
                                  );
                                } catch { return null; }
                              })()}
                              {ref.start_date && !ref.checkout_date && (() => {
                                try {
                                  const sd = parseISO(ref.start_date);
                                  if (!isToday(sd)) {
                                    return (
                                      <span className="text-[10px] text-stone-400">
                                        dal {format(sd, "d MMM", { locale: it })}
                                      </span>
                                    );
                                  }
                                } catch {}
                                return null;
                              })()}
                            </div>

                            {/* Message text inside card */}
                            <div className="text-stone-700 text-sm leading-relaxed">
                              {renderMessageContent(msg.content, msg.room_references)}
                            </div>

                            {/* Comments inside the task card */}
                            {ref.comments && ref.comments.length > 0 && (
                              <div className="mt-1.5 space-y-1 border-t border-stone-200/60 pt-1.5">
                                {ref.comments.map((comment) => (
                                  <div key={comment.id} className="text-xs bg-white/60 rounded p-1.5 group/comment">
                                    <div className="flex items-center gap-1 text-stone-400 mb-0.5">
                                      <span className="font-medium text-stone-600">{comment.user_name}</span>
                                      <span>•</span>
                                      <span>{format(parseISO(comment.created_at), "d MMM HH:mm", { locale: it })}</span>
                                      {(comment.user_id === user?.id || ["admin", "super_admin"].includes(user?.role)) && (
                                        <span className="ml-auto flex gap-1 opacity-0 group-hover/comment:opacity-100 transition-opacity">
                                          <button
                                            onClick={() => {
                                              const newText = window.prompt("Modifica commento:", comment.text);
                                              if (newText !== null && newText.trim()) {
                                                handleEditComment(ref.id, comment.id, newText);
                                              }
                                            }}
                                            className="text-blue-400 hover:text-blue-600"
                                            title="Modifica commento"
                                          >
                                            <Pencil className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteComment(ref.id, comment.id)}
                                            className="text-red-400 hover:text-red-600"
                                            title="Elimina commento"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-stone-700">{comment.text}</p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Close info */}
                            {ref.status === "closed" && (
                              <div className="text-[11px] text-emerald-600 mt-1 border-t border-emerald-200/60 pt-1">
                                Chiuso da {ref.closed_by_name || "N/A"} il{" "}
                                {ref.closed_at ? format(parseISO(ref.closed_at), "d MMM yyyy", { locale: it }) : ""}
                                {ref.closing_note && (
                                  <span className="block italic text-stone-500">"{ref.closing_note}"</span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Message content without tasks */
                      <div className="text-stone-700 text-sm leading-relaxed">
                        {renderMessageContent(msg.content, msg.room_references)}
                      </div>
                    )}

                    {/* Images */}
                    {msg.images && msg.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {msg.images.map((path, idx) => (
                          <a
                            key={idx}
                            href={`${API}/files/${path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={`${API}/files/${path}`}
                              alt="Allegato"
                              className="h-20 w-auto rounded-lg object-cover border border-stone-200"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Message Input Footer */}
      <footer className="fixed bottom-0 left-0 right-0 lg:left-64 max-w-3xl mx-auto bg-white border-t border-stone-200 p-4 pb-safe z-50">
        {isContiAperti ? (
          /* Conti Aperti - specialized input */
          <div className="space-y-2 mb-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="text"
                  value={roomNumber}
                  onChange={(e) => setRoomNumber(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="N° Camera"
                  className="bg-amber-50 border-amber-200 rounded-xl font-mono text-amber-900 placeholder:text-amber-400"
                  data-testid="room-number-input"
                />
              </div>
              <div className="w-[150px]">
                <Input
                  type="date"
                  value={checkoutDate}
                  onChange={(e) => setCheckoutDate(e.target.value)}
                  className="bg-amber-50 border-amber-200 rounded-xl text-amber-900 text-sm"
                  data-testid="checkout-date-input"
                  min={format(new Date(), "yyyy-MM-dd")}
                />
              </div>
            </div>
            <p className="text-xs text-stone-400 ml-1">
              Camera + data check-out. Il conto si chiude automaticamente la notte dopo il check-out
            </p>
          </div>
        ) : isDirect ? null : (
          /* Standard input */
          <div className="mb-3">
            <Input
              type="text"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value.slice(0, 50))}
              placeholder="Parola chiave o camere (es: Piscina, 101,202,303)"
              className="w-full bg-amber-50 border-amber-200 rounded-xl font-mono text-amber-900 placeholder:text-amber-400 focus:ring-amber-300"
              data-testid="room-number-input"
            />
            <p className="text-xs text-stone-400 mt-1 ml-1">
              Max 5 camere con virgola. Il compito parte dal giorno selezionato nel calendario
            </p>
          </div>
        )}

        {/* Image previews */}
        {imagePreviewUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {imagePreviewUrls.map((url, idx) => (
              <div key={idx} className="relative">
                <img
                  src={url}
                  alt="Preview"
                  className="h-16 w-16 object-cover rounded-lg border border-stone-200"
                />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-stone-900 text-white rounded-full flex items-center justify-center"
                  data-testid={`remove-image-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Message input row */}
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            multiple
            className="hidden"
            data-testid="file-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            className="text-stone-400 hover:text-stone-600"
            data-testid="attach-image-button"
          >
            <ImageIcon className="w-5 h-5" />
          </Button>

          <Input
            type="text"
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
            placeholder={isContiAperti ? "Nota (opzionale)" : "Scrivi un messaggio..."}
            className="flex-1 bg-stone-100 border-transparent rounded-full focus:ring-stone-300"
            data-testid="message-input"
          />

          <Button
            type="submit"
            disabled={sending || (isContiAperti ? !roomNumber.trim() || !checkoutDate : !messageContent.trim() && selectedImages.length === 0)}
            size="icon"
            className="rounded-full bg-stone-900 hover:bg-stone-800 text-white"
            data-testid="send-message-button"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </footer>

      {/* Close Task Modal */}
      <Dialog open={closeModalOpen} onOpenChange={setCloseModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Chiudi Passaggio Consegne
            </DialogTitle>
            <DialogDescription>
              Compito{" "}
              <span className="font-mono font-bold text-amber-600">
                #{selectedTask?.room_number}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              value={closingNote}
              onChange={(e) => setClosingNote(e.target.value)}
              placeholder="Nota di chiusura (opzionale)"
              className="resize-none"
              rows={3}
              data-testid="closing-note-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCloseModalOpen(false)}
              data-testid="cancel-close-button"
            >
              Annulla
            </Button>
            <Button
              onClick={handleCloseTask}
              disabled={closingLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-close-button"
            >
              {closingLoading ? "Chiusura..." : "Conferma Chiusura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Comment Modal */}
      <Dialog open={commentModalOpen} onOpenChange={setCommentModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Aggiungi Nota
            </DialogTitle>
            <DialogDescription>
              Compito{" "}
              <span className="font-mono font-bold text-amber-600">
                #{selectedTask?.room_number}
              </span>
              {" "}- Il compito rimarrà aperto
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Scrivi una nota o aggiornamento..."
              className="resize-none"
              rows={3}
              data-testid="comment-text-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCommentModalOpen(false)}
              data-testid="cancel-comment-button"
            >
              Annulla
            </Button>
            <Button
              onClick={handleAddComment}
              disabled={commentLoading || !commentText.trim()}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-comment-button"
            >
              {commentLoading ? "Invio..." : "Aggiungi Nota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatDetail;
